import React, { useState } from 'react';
import apiClient from '../utils/api';
import { Search, MapPin, Package, Truck, CheckCircle, Clock, XCircle } from 'lucide-react';

const STAGES = [
  { key: 'PENDING', label: 'Pending', pct: 0 },
  { key: 'CONFIRMED', label: 'Confirmed', pct: 20 },
  { key: 'DEPARTED', label: 'Departed', pct: 45 },
  { key: 'IN_TRANSIT', label: 'In Transit', pct: 70 },
  { key: 'DELIVERED', label: 'Delivered', pct: 100 },
];

const stagePct = (status: string) => STAGES.find(s => s.key === status)?.pct ?? 0;

const STATUS_ICON: Record<string, React.ReactNode> = {
  PENDING: <Clock size={18} color="#f59e0b" />,
  CONFIRMED: <CheckCircle size={18} color="#6366f1" />,
  DEPARTED: <Truck size={18} color="#0ea5e9" />,
  IN_TRANSIT: <Truck size={18} color="#3b82f6" />,
  DELIVERED: <CheckCircle size={18} color="#22c55e" />,
  CANCELLED: <XCircle size={18} color="#ef4444" />,
  RETURNED: <XCircle size={18} color="#94a3b8" />,
};

export const PublicTracking: React.FC = () => {
  const [input, setInput] = useState('');
  const [shipment, setShipment] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSearch = async (e: React.FormEvent) => {
    e.preventDefault();
    const tn = input.trim().toUpperCase();
    if (!tn) return;
    setLoading(true); setError(''); setShipment(null);
    try {
      const res = await apiClient.publicTracking(tn);
      setShipment(res.data.data);
    } catch (err: any) {
      setError(err.response?.status === 404
        ? `No shipment found for "${tn}"`
        : 'Unable to retrieve tracking info. Please try again.');
    } finally { setLoading(false); }
  };

  const pct = shipment ? stagePct(shipment.status) : 0;
  const cancelled = ['CANCELLED', 'RETURNED'].includes(shipment?.status ?? '');
  const delivered = shipment?.status === 'DELIVERED';
  const moving = ['DEPARTED', 'IN_TRANSIT'].includes(shipment?.status ?? '');

  return (
    <div style={{ minHeight: '100vh', background: 'linear-gradient(135deg, #1e3a5f 0%, #0f172a 100%)', display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '40px 16px' }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 40 }}>
        <div style={{ background: '#3b82f6', borderRadius: 12, padding: 10 }}>
          <Package size={28} color="#fff" />
        </div>
        <div>
          <div style={{ color: '#fff', fontSize: 22, fontWeight: 700 }}>E.H.C Logistics</div>
          <div style={{ color: '#94a3b8', fontSize: 13 }}>Real-time shipment tracking</div>
        </div>
      </div>

      {/* Search box */}
      <div style={{ width: '100%', maxWidth: 560, marginBottom: 32 }}>
        <form onSubmit={handleSearch}>
          <div style={{ display: 'flex', gap: 8 }}>
            <div style={{ position: 'relative', flex: 1 }}>
              <Search size={16} style={{ position: 'absolute', left: 14, top: '50%', transform: 'translateY(-50%)', color: '#94a3b8' }} />
              <input
                value={input}
                onChange={e => setInput(e.target.value)}
                placeholder="Enter tracking number (e.g. LSP-ABC123-XYZ)"
                style={{
                  paddingLeft: 42, width: '100%', height: 48,
                  background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.15)',
                  borderRadius: 10, color: '#fff', fontSize: 14,
                }}
              />
            </div>
            <button
              type="submit"
              disabled={loading}
              style={{
                height: 48, padding: '0 24px', background: '#3b82f6', color: '#fff',
                border: 'none', borderRadius: 10, fontWeight: 600, fontSize: 14, cursor: 'pointer',
                opacity: loading ? 0.7 : 1,
              }}
            >
              {loading ? '...' : 'Track'}
            </button>
          </div>
        </form>
        {error && (
          <div style={{ marginTop: 10, color: '#fca5a5', fontSize: 13, textAlign: 'center' }}>{error}</div>
        )}
      </div>

      {/* Result card */}
      {shipment && (
        <div style={{ width: '100%', maxWidth: 680, background: '#fff', borderRadius: 16, overflow: 'hidden', boxShadow: '0 20px 60px rgba(0,0,0,0.4)' }}>
          {/* Status banner */}
          <div style={{
            padding: '20px 28px',
            background: delivered ? '#f0fdf4' : cancelled ? '#fef2f2' : moving ? '#eff6ff' : '#fefce8',
            borderBottom: '1px solid #e2e8f0',
            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          }}>
            <div>
              <div style={{ fontSize: 12, color: '#64748b', marginBottom: 4 }}>Tracking Number</div>
              <div style={{ fontSize: 18, fontWeight: 700, fontFamily: 'monospace' }}>{shipment.trackingNumber}</div>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              {STATUS_ICON[shipment.status]}
              <span style={{ fontWeight: 700, fontSize: 15 }}>{shipment.status.replace(/_/g, ' ')}</span>
            </div>
          </div>

          <div style={{ padding: '24px 28px' }}>
            {/* Journey bar */}
            <div style={{ marginBottom: 28 }}>
              <div style={{ position: 'relative', height: 12, background: '#e2e8f0', borderRadius: 8, marginBottom: 8 }}>
                <div style={{
                  position: 'absolute', top: 0, left: 0, bottom: 0,
                  width: `${pct}%`,
                  background: cancelled ? '#fca5a5' : delivered ? '#22c55e' : 'linear-gradient(90deg, #3b82f6, #06b6d4)',
                  borderRadius: 8, transition: 'width 0.8s ease',
                }} />
                {STAGES.map(stage => (
                  <div key={stage.key} style={{
                    position: 'absolute', left: `${stage.pct}%`, top: '50%',
                    transform: 'translate(-50%, -50%)',
                    width: 14, height: 14, borderRadius: '50%',
                    background: pct >= stage.pct ? (cancelled ? '#ef4444' : delivered ? '#22c55e' : '#3b82f6') : '#cbd5e1',
                    border: '2px solid #fff',
                    zIndex: 1,
                  }} />
                ))}
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: '#94a3b8' }}>
                {STAGES.map(stage => (
                  <span key={stage.key} style={{ fontWeight: pct >= stage.pct ? 600 : 400, color: pct >= stage.pct ? '#475569' : '#94a3b8' }}>
                    {stage.label}
                  </span>
                ))}
              </div>
            </div>

            {/* Shipment info */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20, marginBottom: 24 }}>
              <div>
                <div style={{ fontSize: 11, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 10 }}>Delivery To</div>
                <div style={{ fontWeight: 600, fontSize: 15 }}>{shipment.receiverName}</div>
                <div style={{ fontSize: 13, color: '#64748b', marginTop: 4 }}>{shipment.receiverAddress}</div>
                {shipment.receiverCity && <div style={{ fontSize: 13, color: '#64748b' }}>{shipment.receiverCity}</div>}
                <div style={{ fontSize: 13, color: '#64748b' }}>{shipment.receiverPhone}</div>
              </div>
              <div>
                <div style={{ fontSize: 11, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 10 }}>Details</div>
                {shipment.estimatedDeliveryTime && shipment.status !== 'DELIVERED' && (
                  <div style={{ fontSize: 13, color: '#64748b', marginBottom: 4 }}>
                    <strong>ETA:</strong> {new Date(shipment.estimatedDeliveryTime).toLocaleDateString()}
                  </div>
                )}
                {shipment.departureTime && (
                  <div style={{ fontSize: 13, color: '#64748b', marginBottom: 4 }}>
                    <strong>Departed:</strong> {new Date(shipment.departureTime).toLocaleString()}
                  </div>
                )}
                {shipment.transporter && (
                  <div style={{ fontSize: 13, color: '#64748b', marginBottom: 4 }}>
                    <strong>Driver:</strong> {shipment.transporter.firstName} {shipment.transporter.lastName}
                  </div>
                )}
                <div style={{ fontSize: 13, color: '#64748b' }}>
                  <strong>Amount:</strong> {Number(shipment.totalAmount).toLocaleString()} RWF
                </div>
              </div>
            </div>

            {/* Tracking timeline */}
            {(shipment.tracking ?? []).length > 0 && (
              <div>
                <div style={{ fontSize: 11, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 14 }}>Tracking History</div>
                <div style={{ position: 'relative', paddingLeft: 20 }}>
                  <div style={{ position: 'absolute', left: 6, top: 0, bottom: 0, width: 2, background: '#e2e8f0' }} />
                  {(shipment.tracking ?? []).map((t: any, i: number) => (
                    <div key={i} style={{ position: 'relative', marginBottom: i < shipment.tracking.length - 1 ? 18 : 0 }}>
                      <div style={{
                        position: 'absolute', left: -20, top: 3,
                        width: 10, height: 10, borderRadius: '50%',
                        background: i === 0 ? '#3b82f6' : '#cbd5e1',
                        border: '2px solid #fff',
                      }} />
                      <div style={{ fontSize: 13, fontWeight: 600, color: '#1e293b' }}>{t.status.replace(/_/g, ' ')}</div>
                      {t.location && <div style={{ fontSize: 12, color: '#64748b', display: 'flex', alignItems: 'center', gap: 4, marginTop: 2 }}><MapPin size={11} />{t.location}</div>}
                      {t.description && <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 2 }}>{t.description}</div>}
                      <div style={{ fontSize: 11, color: '#cbd5e1', marginTop: 2 }}>{new Date(t.createdAt).toLocaleString()}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      <div style={{ marginTop: 40, color: '#475569', fontSize: 12 }}>
        © E.H.C Logistics Platform — <a href="/login" style={{ color: '#3b82f6' }}>Staff Login</a>
      </div>
    </div>
  );
};
